import axios, { AxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { format } from "@redtea/format-axios-error";

import { generate_jwt } from "./generateToken";
import { ensureJwtTicketAndTimestamp } from "./ensureJwtTicketAndTimestamp";
import { NextPageContextEnhanced } from "../types/page";
import { API_HEADERS_CONSTANTS } from "utils/constants";
import { getCustomThemeValue } from "helpers/osHelpers";

export interface IApiHeaders {
  source: "desktop" | "mobile";
  country: string;
  "User-Agent": string;
  "accept-language": "ar" | "en" | string;
  Authorization?: string;
  "x-tracking-uuid"?: string;
  "X-Forwarded-For-Client"?: string;
  "release-version"?: string;
}

export const formatAxiosError: (err: any) => Error = (err) => {
  let outErr: Error;

  if (err instanceof Error) outErr = err;

  if (err == undefined)
    outErr = new Error("Error while trying to use Fetch, no stack available");

  outErr = new Error(JSON.stringify(err));

  return format(outErr);
};

const getApiHeaders = (
  context: NextPageContextEnhanced,
  lang: string = null
): any => {
  ensureJwtTicketAndTimestamp(context);
  let authHeader;

  const auth = generate_jwt(context.getData("tgtTicket"), {
    sub: context.getData("member_id") || context.getData("deviceUUID"),
    aud: context.getData("source"),
    at0: context.getData("tgtTimestamp"),
  });
  authHeader = { Authorization: `Bearer ${auth}` };

  const defaultCurrency = context?.countryObject?.currencies?.filter(currency=> currency?.id == context.countryObject?.default_currency)?.[0]?.shorthand_en;
  return  {
    source: context.getData("source"),
    country: context.countryCode || "jo", //in case some screens, we don't have Country yet, like in choose country page
    "accept-language": lang || context.lang,
    "X-Forwarded-For-Client": context.userIP,
    "User-Agent": context.userAgent,
    "x-tracking-uuid": context.getData("deviceUUID"),
    "release-version": API_HEADERS_CONSTANTS.RELEASE_VERSION,
    "session-id": context.getData("sessionId"),
    ...( !defaultCurrency ? { currency: defaultCurrency } : {}),
    "x-domain-theme": getCustomThemeValue("domainTheme"),
    ...authHeader,
  };

};

const handle401 = (error, context: NextPageContextEnhanced, url: string) => {
  console.log("handle401 Error->:", error?.response?.status, url);
  ensureJwtTicketAndTimestamp(context, true);
};

export const __postServerSide = async (
  url: string,
  data: any = {},
  context: NextPageContextEnhanced,
  lang: string = null
): Promise<any> => {
  url = url.indexOf("http") === 0 ? url : `${process.env.API_URL}/${url}`;

  const headers = getApiHeaders(
    context,
    lang
  ) as unknown as AxiosRequestHeaders;
  headers["content-type"] = "application/json;charset=UTF-8";

  const axiosConfig: AxiosRequestConfig = {
    url,
    method: "POST",
    headers,
    data,
  };

  console.log("__postServerSide:", url, data);
  return axios(axiosConfig).catch((error) => {
    console.log("-> error?.response?.status", error?.response?.status);
    console.log("-> error?.response?.data", error?.response?.data);
    throw formatAxiosError(error);
  });
};

export const __fetchServerSide = async (
  url: string,
  context?: NextPageContextEnhanced,
  cacheTimeInSeconds?: string | number,
  mapper: (any) => any = null,
  forceFresh: boolean = false,
  lang: string = null
): Promise<any> => {
  url = url.indexOf("http") === 0 ? url : `${process.env.API_URL}/${url}`;
  const headers = getApiHeaders(context, lang);
  return context.cacheManager
    .fetchWithCache(
      url,
      lang || context.lang,
      axios.get,
      headers,
      cacheTimeInSeconds,
      mapper,
      forceFresh
    )
    .catch((error) => {
      console.log("-> error?.response?.status", error?.response?.status);
      if ([301, 302, 410].indexOf(error?.response?.status) > -1) {
        throw error;
      }
      if (error?.response?.status === 401) {
        console.error("401 error", {
          url,
          authorization: headers['Authorization'],
          cookies: context.cookies,
          tgt_timestamp: context.getData("tgtTimestamp"),
          tgt_ticket: context.getData("tgtTicket"),
          deviceUUID: context.getData("deviceUUID"),
        })
        handle401(error, context, url);
        return __fetchServerSide(url, context);
      }
      console.log(JSON.stringify(formatAxiosError(error), null, 2));

      throw formatAxiosError(error);
    });
};
export const __fetchVerticalServerSide = async (
  url: string,
  context: NextPageContextEnhanced,
  cacheTimeInSeconds?: string | number,
  mapper: (any) => any = null,
  forceFresh: boolean = false,
  lang: string = null
): Promise<any> => {
  url =
    url.indexOf("http") === 0 ? url : `${process.env.API_VERTICAL_URL}/${url}`;

  const headers = getApiHeaders(context, lang);

  const responseData = await axios.get(url, { headers }).then((res) => {
    return res.data;
  });
  return responseData;
};

export const __fetchArrayBuffer = async (
  url: string,
  context: NextPageContextEnhanced,
  cacheTimeInSeconds?: string | number,
  mapper: (any) => any = null,
  forceFresh: boolean = false,
  lang: string = null
): Promise<any> => {
  url =
    url.indexOf("http") === 0 ? url : `${process.env.API_VERTICAL_URL}/${url}`;

  const headers = getApiHeaders(context, lang);
  headers["Content-Type"] = "application/pdf";
  const responseData = await axios
    .get(url, {
      headers,
      responseType: "arraybuffer",
    })
    .then((res) => {
      const headers = res.headers;
      const data = res.data;
      return { data, headers };
    });
  return responseData;
};
